'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';

import { callApi } from '~/app/(app)/[lng]/helpers/api-helpers';
import { getUserMenuItems } from '~/shared/configs/menus';
import { RootContext } from '~/shared/configs/root-context';
import { getLocaleFromPathname } from '~/shared/helpers/18n-helpers';
import { onSignOut } from '~/shared/helpers/browser-helpers';
import {
  checkIsAdminPanel,
  checkIsClientsPanel,
  checkIsNoLayout,
  checkIsPlatform,
} from '~/shared/helpers/check-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import { useBrowserStorageValue } from '~/shared/hooks/use-browser-storage-value';
import { useAppDispatch, useAppSelector } from '~/shared/store/hooks';
import { changeStateOfConfirmModal, changeStateOfModal, setUser } from '~/shared/store/slices/app/app-slice';
import { getAppUser, getModal, getStateOfConfirmModal } from '~/shared/store/slices/app/selectors';
import { getShowHeader } from '~/shared/store/slices/lessons/selectors';
import { getCurrentUser } from '~/shared/store/slices/users/selectors';
import { setCurrentUser } from '~/shared/store/slices/users/users-slice';

type NavigationOnBehalfArgs = Readonly<{
  _id: string;
  course: string;
  sessionKey: string;
}>;

type UseLayoutProps = Readonly<{
  lng: string;
}>;

type ConfirmModalPayload = Parameters<typeof changeStateOfConfirmModal>[0];
type ModalPayload = Parameters<typeof changeStateOfModal>[0];

const useLayout = ({ lng }: UseLayoutProps) => {
  const pathname = usePathname();
  const effectiveLng = useMemo(() => getLocaleFromPathname(pathname) ?? lng, [pathname, lng]);

  const dispatch = useAppDispatch();

  const showHeader = useAppSelector(getShowHeader);
  const userFromStore = useAppSelector(getAppUser);
  const modal = useAppSelector(getModal);
  const stateOfConfirmModal = useAppSelector(getStateOfConfirmModal);
  const rootContext = useContext(RootContext);
  const [isMainMenuToggled, setIsMainMenuToggled] = useState<boolean>(false);
  const user = useAppSelector(getAppUser);
  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);
  const { isNoLayout, isNoFooter } = checkIsNoLayout(pathname);
  const shouldShowHeader = !isNoLayout && showHeader;
  const shouldShowFooter = !isNoLayout && !isNoFooter;
  const currentCourse = useBrowserStorageValue('currentCourse');
  const sessionKey = useBrowserStorageValue('sessionKey');
  const userId = user?._id;
  const currentUser = useAppSelector(getCurrentUser);
  const currentUserId = useBrowserStorageValue('currentUser');
  const t = useTypedTranslation(['modal', 'button', 'page', 'text']);
  const router = useRouter();
  const isAuthenticated = Boolean(user);
  const isClientsPanel = checkIsClientsPanel(pathname);

  const toggleMainMenu = () => {
    setIsMainMenuToggled((prev) => !prev);
  };

  const onMobileMenuClose = () => {
    setIsMainMenuToggled(false);
  };

  const onChangeStateOfModal = (payload: ModalPayload) => {
    dispatch(changeStateOfModal(payload));
  };

  const onChangeStateOfConfirmModal = (payload: ConfirmModalPayload) => {
    dispatch(changeStateOfConfirmModal(payload));
  };

  useEffect(() => {
    if (currentUserId) {
      (async () => {
        const result = await dispatch(
          callApi('findByIdUser', {
            _id: currentUserId,
          }),
        );
        dispatch(setCurrentUser(result?.data?.data?.findByIdUserQuery));
      })();
    }
  }, [currentUserId]);

  const onSignOutCallback = () => {
    onSignOut({
      currentUser,
      dispatch,
      userId,
      currentCourse,
      sessionKey,
      isClientsPanel,
      effectiveLng,
      t,
      rootContext,
      router,
      lng,
    });
  };

  const menuItems = getUserMenuItems({
    isAuthenticated,
    onMobileMenuClose,
    rootContext,
    isClientsPanel,
    onSignOut: onSignOutCallback,
    lng: effectiveLng,
    dispatch,
    user,
    t,
    router,
    isPlatform,
    isAdminPanel,
  });

  return {
    onMobileMenuClose,
    stateOfConfirmModal,
    onChangeStateOfConfirmModal,
    isMainMenuToggled,
    toggleMainMenu,
    rootContext,
    showHeader,
    onChangeStateOfModal,
    modal,
    isAdminPanel,
    dispatch,
    userFromStore,
    user,
    isNoLayout,
    t,
    isPlatform,
    isClientsPanel,
    effectiveLng,
    isNoFooter,
    shouldShowFooter,
    shouldShowHeader,
    onSignOutCallback,
    menuItems,
  };
};

export default useLayout;
